import React from 'react';
import { message, Popconfirm, Tooltip } from 'antd';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  CardinalityValue,
  CombinationText,
  CombinationValue,
  CombinationWrapper,
  CombinationValueText,
  TopIconSection,
} from '../styled';
import { CombinationProp } from '../types';
import {
  getCardinalityText,
  getExtensionText,
  getHighlightColor,
  isFirstDimension,
  getDeletionCombination,
  getDeleteText,
  getDimensionNameList,
  getHighLightCombinations,
} from '../utils';
import { highlightDimensionAtom, isConfigLoadingAtom } from '../atoms';
import { MAX_ALLOWED_LEVEL } from '../constants';
import { useUpdateConfigList, useUpdateConfigListForMetric } from '../hooks/useUpdateConfigList';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector, aiAlertFusionVsiSelector } from '@src/store/common/selectors';
import { aiCohortDrawerAtom } from '@src/components/ai-alerts/HeaderPanel/SensitivityControl/atoms';

export default function CombinationCard({ combinationSet, aiConfigList, level }: CombinationProp) {
  const isAdminUser = useSelector(isAdminRoleSelector);
  const isAiAlertFusionVsiOn = useSelector(aiAlertFusionVsiSelector);
  const { name, backendName, cardinality, isCustomDimension, isBeingAdded, isBeingDeleted } =
    combinationSet;
  const combinationList = getDimensionNameList(name);
  const [highlightDimensions, setHighlightDimension] = useAtom(highlightDimensionAtom);
  const setIsConfigLoading = useSetAtom(isConfigLoadingAtom);
  const shallDelete = getDeletionCombination(highlightDimensions, backendName);
  const dimensionText = isFirstDimension(level) ? 'this dimension' : 'these dimensions';
  const extensionText = getExtensionText(highlightDimensions);
  const isCardinalityFetching = useAtomValue(isConfigLoadingAtom);
  const isValidLevel = level < MAX_ALLOWED_LEVEL;
  const displayDeleteBtn =
    (isCustomDimension || isAiAlertFusionVsiOn) && isValidLevel && !isBeingDeleted;
  const cardinalityHoverText = getDeleteText(isBeingAdded, isBeingDeleted);
  const popupText = `Are you sure to remove ${dimensionText}? ${highlightDimensions?.length > 1 ? 'This will also remove the other cohorts highlighted as they are linked to this.' : ''}`;

  const highlightColor = getHighlightColor(shallDelete, isBeingDeleted);

  const onDimensionRemoveSuccess = () => {
    message.open({
      content: `${extensionText} deleted`,
      type: 'success',
      style: { textTransform: 'capitalize' },
    });
    // Note: Don't set loading to false here, let the refetch handle it
  };

  const onDimensionRemoveError = () => {
    message.open({
      content: `unable to delete ${extensionText}`,
      type: 'error',
      style: { textTransform: 'capitalize' },
    });

    setIsConfigLoading(false);
  };

  const onPopToggle = (open) => {
    if (open || !highlightDimensions.length) return;

    setHighlightDimension([]);
  };

  const aiCohortDrawerConfig = useAtomValue(aiCohortDrawerAtom);
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);

  // Always call both hooks to avoid conditional hook calls
  const generalMutation = useUpdateConfigList(onDimensionRemoveSuccess, onDimensionRemoveError);
  const metricMutation = useUpdateConfigListForMetric(
    onDimensionRemoveSuccess,
    onDimensionRemoveError,
    aiCohortDrawerConfig?.metrics || [],
  );

  // Choose which mutation to use based on feature flag
  const { mutate: mutateAiAlertsConfig } = enableDPICustomDimension
    ? metricMutation
    : generalMutation;

  const removeHighlightedDimensions = () => {
    setIsConfigLoading(true);
    const aiAlertsConfig = aiConfigList?.map((levelData) => {
      const { dimensionData = [] } = levelData;
      const updatedDimensionData = dimensionData.filter(({ backendName }) => {
        return !highlightDimensions.some(
          ({ backendName: deletedBackendName }) => deletedBackendName === backendName,
        );
      });

      return { ...levelData, dimensionData: updatedDimensionData };
    });
    const metrics = aiCohortDrawerConfig?.metrics || [];
    mutateAiAlertsConfig({ aiAlertsConfig, metrics });
    setHighlightDimension([]);
  };

  const onConfirm = () => {
    removeHighlightedDimensions();
  };

  const checkSubsequentCombinations = (aiConfigList, combination, level, backendName) => {
    const highlightCombinations = getHighLightCombinations(
      aiConfigList,
      combination,
      level,
      backendName,
      isAiAlertFusionVsiOn,
    );
    setHighlightDimension(highlightCombinations);
  };

  const handleDeleteClick = () => {
    if (!isAdminUser) {
      return;
    }
    checkSubsequentCombinations(aiConfigList, combinationList, level, backendName);
  };

  return (
    <CombinationWrapper highlightDimension={highlightColor} isBeingDeleted={isBeingDeleted}>
      <TopIconSection>
        {displayDeleteBtn && (
          <Popconfirm
            placement="top"
            disabled={!isAdminUser}
            title={popupText}
            style={{ width: 'max-content' }}
            onConfirm={onConfirm}
            okText="Delete"
            cancelText="No"
            onOpenChange={onPopToggle}
            getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
            overlayStyle={{ width: 'max-content' }}
          >
            <CombinationText
              style={{ cursor: isAdminUser ? 'pointer' : 'not-allowed' }}
              onClick={handleDeleteClick}
            >
              <CloseOutlined color="red" />
            </CombinationText>
          </Popconfirm>
        )}
      </TopIconSection>
      <CombinationValue
        isCustom={isCustomDimension}
        alignItems={combinationList.length > 1 ? 'flex-start' : 'center'}
      >
        {combinationList.map((combName, ind) => (
          <CombinationValueText key={ind}>{combName}</CombinationValueText>
        ))}
      </CombinationValue>
      <Tooltip title={cardinalityHoverText}>
        <CardinalityValue>
          {getCardinalityText(
            cardinality,
            level,
            isCardinalityFetching,
            false,
            isBeingAdded,
            isBeingDeleted,
          )}
          {isBeingAdded || isBeingDeleted ? (
            <ExclamationCircleOutlined style={{ marginLeft: '4px' }} />
          ) : null}
        </CardinalityValue>
      </Tooltip>
    </CombinationWrapper>
  );
}
